import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link, useRoute, useLocation } from "wouter";
import { scrollToTop } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Calendar, 
  Clock, 
  Share2, 
  ChevronRight, 
  Facebook, 
  Twitter, 
  Linkedin,
  Mail,
  MessageSquare,
  Eye,
  BookOpen
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { toast } from "@/hooks/use-toast";
import { BlogPost, BlogCategory, BlogComment, User, InsertBlogComment } from "@shared/schema";

export default function BlogPostPage() {
  const [, params] = useRoute("/blog/:slug");
  const [, navigate] = useLocation();
  
  const slug = params?.slug || "";
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  
  useEffect(() => {
    scrollToTop();
    
    // When the component mounts, increment the view count for this post
    if (slug) {
      const viewIncrement = async () => {
        try {
          await apiRequest("POST", `/api/blog/${slug}/view`);
        } catch (error) {
          console.error("Failed to increment view count:", error);
        }
      };
      viewIncrement();
    }
  }, [slug]);
  
  // Fetch blog post data
  const { 
    data: post, 
    isLoading,
    error
  } = useQuery<BlogPost>({
    queryKey: [`/api/blog/${slug}`],
    enabled: !!slug,
  });
  
  // Redirect to 404 if post not found
  useEffect(() => {
    if (error) {
      navigate("/not-found", { replace: true });
    }
  }, [error, navigate]);
  
  // Fetch categories
  const { data: categories = [] } = useQuery<BlogCategory[]>({
    queryKey: ["/api/blog/categories"],
  });
  
  // Fetch author data
  const { data: author } = useQuery<User>({
    queryKey: [`/api/users/${post?.authorId}`],
    enabled: !!post?.authorId,
  });
  
  // Fetch related posts
  const { data: relatedPosts = [] } = useQuery<BlogPost[]>({
    queryKey: ["/api/blog/related", post?.id, post?.categoryId],
    enabled: !!post?.id && !!post?.categoryId,
  });
  
  // Fetch comments for this post
  const { 
    data: comments = [],
    refetch: refetchComments
  } = useQuery<BlogComment[]>({
    queryKey: [`/api/blog/${slug}/comments`],
    enabled: !!slug,
  });
  
  // Submit comment mutation
  const commentMutation = useMutation({
    mutationFn: (commentData: Partial<InsertBlogComment>) => {
      return apiRequest("POST", `/api/blog/${slug}/comments`, commentData);
    },
    onSuccess: () => {
      toast({
        title: "Comment submitted!",
        description: "Your comment is awaiting approval.",
      });
      setComment("");
      setName("");
      setEmail("");
      refetchComments();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to submit comment",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    },
  });
  
  // Share handlers
  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = post?.title || "Blog Post";
    
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`, '_blank');
        break;
      case 'email':
        window.open(`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`, '_blank');
        break;
      default:
        if (navigator.share) {
          navigator.share({
            title,
            url
          });
        } else {
          navigator.clipboard.writeText(url);
          toast({
            title: "Link copied!",
            description: "The link has been copied to your clipboard.",
          });
        }
    }
  };
  
  // Helper function to format dates
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // Get category name by ID
  const getCategoryName = (categoryId: number) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : "Uncategorized";
  };
  
  // Handle comment submission
  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!post?.id) return;
    
    commentMutation.mutate({
      postId: post.id,
      authorName: name,
      authorEmail: email,
      content: comment
    });
  };
  
  if (isLoading) {
    return (
      <div className="pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse rounded-lg bg-gray-100 h-12 mb-4 w-3/4"></div>
            <div className="animate-pulse rounded-lg bg-gray-100 h-12 mb-8 w-1/2"></div>
            <div className="animate-pulse rounded-lg bg-gray-100 h-64 mb-6"></div>
            <div className="animate-pulse rounded-lg bg-gray-100 h-4 mb-2 w-full"></div>
            <div className="animate-pulse rounded-lg bg-gray-100 h-4 mb-2 w-full"></div>
            <div className="animate-pulse rounded-lg bg-gray-100 h-4 mb-2 w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!post) return null;
  
  return (
    <>
      <div className="pt-24 pb-12 bg-[#f8f9fa]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
              <Link href="/blog" className="hover:text-[#7d9b76]">Blog</Link>
              <ChevronRight className="h-4 w-4" />
              <Link 
                href={`/blog/category/${categories.find(c => c.id === post.categoryId)?.slug || ""}`}
                className="hover:text-[#7d9b76]"
              >
                {getCategoryName(post.categoryId)}
              </Link>
              <ChevronRight className="h-4 w-4" />
              <span className="text-gray-400 truncate">{post.title}</span>
            </div>
            
            <h1 className="font-serif text-3xl md:text-4xl font-bold text-[#1a365d] mb-4">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <Badge variant="outline" className="bg-[rgba(125,155,118,0.1)] text-[#7d9b76]">
                {getCategoryName(post.categoryId)}
              </Badge>
              
              <div className="flex items-center text-sm text-gray-500">
                <Calendar className="h-4 w-4 mr-1" />
                <span>{formatDate(post.publishedAt)}</span>
              </div>
              
              <div className="flex items-center text-sm text-gray-500">
                <Clock className="h-4 w-4 mr-1" />
                <span>{post.readTime} min read</span>
              </div>
              
              <div className="flex items-center text-sm text-gray-500">
                <Eye className="h-4 w-4 mr-1" />
                <span>{post.viewCount} views</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {post.imageUrl && (
        <div className="py-8 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="rounded-lg overflow-hidden">
                <img 
                  src={post.imageUrl}
                  alt={post.imageAlt || post.title}
                  className="w-full h-auto"
                  loading="eager"
                  fetchpriority="high"
                />
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center">
                <Avatar className="h-10 w-10 mr-3">
                  <AvatarImage src={author?.profileImage || ""} alt={author?.username || "Author"} />
                  <AvatarFallback>{author?.username?.[0] || "A"}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{author?.username || "SW Accounting"}</p>
                  <p className="text-sm text-gray-500">Accounting Professional</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex items-center gap-2"
                  onClick={() => handleShare('default')}
                >
                  <Share2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Share</span>
                </Button>
                
                <div className="hidden sm:flex items-center ml-2 gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0" 
                    onClick={() => handleShare('facebook')}
                  >
                    <Facebook className="h-4 w-4" />
                    <span className="sr-only">Share on Facebook</span>
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0" 
                    onClick={() => handleShare('twitter')}
                  >
                    <Twitter className="h-4 w-4" />
                    <span className="sr-only">Share on Twitter</span>
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0" 
                    onClick={() => handleShare('linkedin')}
                  >
                    <Linkedin className="h-4 w-4" />
                    <span className="sr-only">Share on LinkedIn</span>
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0" 
                    onClick={() => handleShare('email')}
                  >
                    <Mail className="h-4 w-4" />
                    <span className="sr-only">Share via Email</span>
                  </Button>
                </div>
              </div>
            </div>
            
            <article className="prose max-w-none prose-headings:font-serif prose-headings:text-[#1a365d] prose-p:text-gray-700 prose-a:text-[#7d9b76] prose-a:no-underline hover:prose-a:text-[#1a365d] prose-img:rounded-lg mb-12">
              <div dangerouslySetInnerHTML={{ __html: post.content }} />
            </article>
            
            <div className="border-y border-gray-200 py-6 mb-12">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Share this article:</span>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="icon" 
                      className="h-8 w-8 rounded-full" 
                      onClick={() => handleShare('facebook')}
                    >
                      <Facebook className="h-4 w-4" />
                      <span className="sr-only">Share on Facebook</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon" 
                      className="h-8 w-8 rounded-full" 
                      onClick={() => handleShare('twitter')}
                    >
                      <Twitter className="h-4 w-4" />
                      <span className="sr-only">Share on Twitter</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon" 
                      className="h-8 w-8 rounded-full" 
                      onClick={() => handleShare('linkedin')}
                    >
                      <Linkedin className="h-4 w-4" />
                      <span className="sr-only">Share on LinkedIn</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon" 
                      className="h-8 w-8 rounded-full" 
                      onClick={() => handleShare('email')}
                    >
                      <Mail className="h-4 w-4" />
                      <span className="sr-only">Share via Email</span>
                    </Button>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <Link href="#comments" className="flex items-center text-gray-600 hover:text-[#7d9b76]">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    {comments.length} comment{comments.length !== 1 ? 's' : ''}
                  </Link>
                </div>
              </div>
            </div>
            
            <div className="mb-16">
              <h2 className="font-serif text-2xl font-bold text-[#1a365d] mb-6">Related Articles</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedPosts.length > 0 ? (
                  relatedPosts.slice(0, 2).map(relatedPost => (
                    <div key={relatedPost.id} className="bg-[#f8f9fa] rounded-lg p-6 group">
                      <Badge 
                        variant="outline" 
                        className="mb-3 bg-white text-[#7d9b76]"
                      >
                        {getCategoryName(relatedPost.categoryId)}
                      </Badge>
                      <h3 className="font-serif text-lg font-bold text-[#1a365d] mb-2 group-hover:text-[#7d9b76] transition-colors">
                        <Link href={`/blog/${relatedPost.slug}`} className="block">
                          {relatedPost.title}
                        </Link>
                      </h3>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{relatedPost.summary}</p>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{formatDate(relatedPost.publishedAt)}</span>
                        <span className="text-gray-500">{relatedPost.readTime} min read</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-2 text-center py-8 bg-[#f8f9fa] rounded-lg">
                    <BookOpen className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-500">No related articles found</p>
                  </div>
                )}
              </div>
            </div>
            
            <div id="comments" className="scroll-mt-24">
              <Separator className="mb-8" />
              
              <h2 className="font-serif text-2xl font-bold text-[#1a365d] mb-6">
                Comments ({comments.length})
              </h2>
              
              {comments.length > 0 ? (
                <div className="space-y-6 mb-8">
                  {comments.map(comment => (
                    <div key={comment.id} className="bg-[#f8f9fa] rounded-lg p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center">
                          <Avatar className="h-8 w-8 mr-3">
                            <AvatarFallback>{comment.authorName[0]}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{comment.authorName}</p>
                            <p className="text-xs text-gray-500">{formatDate(comment.createdAt)}</p>
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-700">{comment.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-[#f8f9fa] rounded-lg p-8 text-center mb-8">
                  <MessageSquare className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-500 mb-2">No comments yet</p>
                  <p className="text-sm text-gray-400">Be the first to share your thoughts!</p>
                </div>
              )}
              
              <div className="bg-[#f8f9fa] rounded-lg p-6">
                <h3 className="font-serif text-xl font-bold text-[#1a365d] mb-4">Leave a Comment</h3>
                
                <form onSubmit={handleCommentSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Name *
                      </label>
                      <input
                        id="name"
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#7d9b76]"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email * (will not be published)
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#7d9b76]"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-1">
                      Comment *
                    </label>
                    <textarea
                      id="comment"
                      rows={4}
                      value={comment}
                      onChange={e => setComment(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#7d9b76]"
                      required
                    ></textarea>
                  </div>
                  
                  <div className="text-sm text-gray-500 mb-4">
                    <p>Your comment will be reviewed before it appears.</p>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="bg-[#7d9b76] hover:bg-opacity-90"
                    disabled={commentMutation.isPending}
                  >
                    {commentMutation.isPending ? "Submitting..." : "Submit Comment"}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="py-16 bg-[#f8f9fa]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-2xl font-bold text-[#1a365d] mb-4">
              Need Professional Accounting Help?
            </h2>
            <p className="text-gray-700 mb-8">
              Our team in Draper, Utah offers expert accounting services for businesses nationwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                href="/contact"
                className="bg-white text-[#1a365d] hover:bg-gray-50 px-6 py-3 rounded-md font-semibold border border-gray-200"
              >
                Ask a Question
              </Link>
              <Link 
                href="/booking"
                className="bg-[#1a365d] text-white hover:bg-opacity-90 px-6 py-3 rounded-md font-semibold"
              >
                Book a Consultation
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}